import React, { useEffect, useState } from "react";
import * as Yup from "yup";
import MDBox from "components/MDBox";
import {
  Alert,
  Box,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Modal,
  Select,
  Typography,
  TextField,
  Button,
} from "@mui/material";
import MDButton from "components/MDButton";
import { Field, Form, Formik } from "formik";
import api from "API/api";
import { CloseOutlined, Info } from "@mui/icons-material";
import ReactPolygonDrawer from "react-polygon-drawer";
import { toast } from "sonner";
import NoImage from "../../../assets/images/Image-not-found.png";
import { S3SignedUrl } from "components/Common/s3Bucket";
import colors from "assets/theme/base/colors";

const AddScenarioModal = ({
  sitesData,
  addScenarioOpen,
  editScenarioData,
  setAddScenarioOpen,
  getSitesList,
}) => {
  const { white, gradients } = colors;
  const [services, setServices] = useState([]);
  const [selectedService, setSelectedService] = useState("");
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [polygons, setPolygons] = useState({});
  const [drawerKey, setDrawerKey] = useState(0);
  const [pendingPolygon, setPendingPolygon] = useState(null);
  const [showClassSelector, setShowClassSelector] = useState(false);
  const [areaName, setAreaName] = useState("");

  const style = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 650,
    bgcolor: "background.paper",
    boxShadow: 24,
    borderRadius: "14px",
    outline: "none !important",
  };

  const validationSchema = Yup.object().shape({
    device: Yup.string().required("Device is required"),
    selectedSubServices: Yup.array()
      .min(1, "At least one sub-service must be selected")
      .required("Sub-services are required"),
    areas: Yup.mixed().nullable(),
  });

  useEffect(() => {
    const fetchSignedUrls = async () => {
      const fetches = await Promise.all(
        sitesData?.map(async (data) => {
          if (data?.thumbnail) {
            try {
              const key = data.thumbnail.replace(
                "https://benchmark-blue-media.s3.amazonaws.com/",
                ""
              );
              const signedUrl = await S3SignedUrl("benchmark-blue-media", key);
              return { ...data, url: signedUrl };
            } catch (err) {
              console.error(
                `Failed to fetch signed URL for ${data.thumbnail}`,
                err
              );
            }
          }
          return data;
        })
      );
      setData(fetches);
    };

    if (sitesData?.length) {
      fetchSignedUrls();
    }
  }, [sitesData]);

  const initialValues = {
    type: editScenarioData ? editScenarioData.type : "",
    device: editScenarioData ? editScenarioData.device_id?.toString() : "",
    provided_service_type: editScenarioData
      ? editScenarioData.provided_service_id?.toString()
      : "",
    selectedSubServices:
      editScenarioData?.sub_service_type?.map((sub) => sub.id) || [],
    areas: "",
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    const formData = new FormData();
    if (editScenarioData) {
      formData.append("service_id", editScenarioData?.service_id);
    }
    formData.append("device_id", Number(values.device));
    formData.append("service_type_id", values.provided_service_type);
    formData.append("sub_services", JSON.stringify(values.selectedSubServices));

    const areasData =
      Object.keys(polygons).length > 0 ? JSON.stringify(polygons) : "";
    formData.append("areas", areasData);

    const endpoint = "/cv/scenarioapi";
    const method = editScenarioData ? "put" : "post";
    try {
      setLoading(true);
      const res = await api[method](endpoint, formData);
      toast.success(res.data.message);
    } catch (error) {
      toast.error(error.response?.data?.message || "Submission Failed");
    } finally {
      setSubmitting(false);
      setAddScenarioOpen(false);
      getSitesList();
      setLoading(false);
    }
  };

  const handleServiceChange = (e, setFieldValue) => {
    const selectedServiceId = e.target.value;
    setFieldValue("provided_service_type", selectedServiceId);
    setSelectedService(selectedServiceId);

    const selectedServiceData = services?.find(
      (service) => service.id === parseInt(selectedServiceId)
    );

    if (selectedServiceData) {
      const allSubServiceIds = selectedServiceData?.sub_services?.map(
        (subService) => subService[1]
      );

      setFieldValue("selectedSubServices", allSubServiceIds);
    }
  };

  const handleSubServiceChange = (id, setFieldValue, values) => {
    const updatedSubServices = values.selectedSubServices.includes(id)
      ? values.selectedSubServices.filter((sid) => sid !== id)
      : [...values.selectedSubServices, id];
    setFieldValue("selectedSubServices", updatedSubServices);
  };

  const getServiceList = async () => {
    try {
      const res = await api.get(`/cv/scenario-details`);
      setServices(res?.data?.data);
    } catch (error) {
      console.error("Error fetching services:", error);
    }
  };

  const handlePolygonComplete = (data) => {
    if (!Array.isArray(data) || data.length === 0) return;

    const newPolygon = data.map((point) => ({
      x: (point.x / 480).toFixed(5),
      y: (point.y / 300).toFixed(5),
    }));

    setPendingPolygon(newPolygon);
    setShowClassSelector(true);
    setAreaName("");
  };

  const handleAreaNameSubmit = (setFieldValue) => {
    if (!pendingPolygon || !areaName.trim()) {
      toast.error("Please enter a valid area name");
      return;
    }

    if (polygons[areaName.trim()]) {
      toast.error("Area name already exists. Please choose a different name.");
      return;
    }

    setPolygons((prev) => {
      const updated = { ...prev };
      updated[areaName.trim()] = pendingPolygon;
      return updated;
    });

    setFieldValue("areas", polygons);

    setPendingPolygon(null);
    setShowClassSelector(false);
    setAreaName("");
    setDrawerKey((prev) => prev + 1);
    toast.success("Area added successfully");
  };

  const handleDeletePolygon = (className, setFieldValue) => {
    setPolygons((prev) => {
      const updated = { ...prev };
      delete updated[className];
      setFieldValue("areas", updated);
      return updated;
    });
    toast.success("Area deleted successfully");
  };

  const handleCancelAreaSelection = () => {
    setPendingPolygon(null);
    setShowClassSelector(false);
    setAreaName("");
    setDrawerKey((prev) => prev + 1);
  };

  useEffect(() => {
    getServiceList();
  }, []);

  useEffect(() => {
    if (editScenarioData) {
      setSelectedService(editScenarioData?.provided_service_id);
      if (editScenarioData.areas) {
        try {
          const parsedAreas =
            typeof editScenarioData.areas === "string"
              ? JSON.parse(editScenarioData.areas)
              : editScenarioData.areas;
          setPolygons(parsedAreas);
        } catch (error) {
          console.error("Error parsing areas:", error);
        }
      }
    } else {
      setSelectedService("");
      setPolygons({});
    }
  }, [editScenarioData]);

  useEffect(() => {
    const updateCanvasSize = () => {
      const imageElement = document.querySelector(".polygon-image");
      const canvasElement = document.querySelector(".polygon-drawer canvas");

      if (imageElement && canvasElement) {
        const { width, height } = imageElement.getBoundingClientRect();
        canvasElement.width = width;
        canvasElement.height = height;
      }
    };

    updateCanvasSize();

    window.addEventListener("resize", updateCanvasSize);
    return () => window.removeEventListener("resize", updateCanvasSize);
  }, [selectedService]);

  return (
    <React.Fragment>
      <Modal open={addScenarioOpen} onClose={() => setAddScenarioOpen(false)}>
        <Box sx={style} className="add-scenario-modal">
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({ setFieldValue, isSubmitting, values, dirty }) => (
              <Form>
                <Box
                  sx={{
                    mb: 3,
                    backgroundColor: gradients.info.main,
                    borderTopLeftRadius: "14px",
                    borderTopRightRadius: "14px",
                  }}
                >
                  <Box
                    sx={{
                      padding: "15px",
                      display: "flex",
                      justifyContent: "space-between",
                      color: white.main,
                    }}
                  >
                    <Typography sx={{ fontSize: "18px", fontWeight: "500" }}>
                      {editScenarioData ? "EDIT SCENARIO" : "ADD SCENARIO"}
                    </Typography>
                    <CloseOutlined
                      onClick={() => setAddScenarioOpen(false)}
                      sx={{ cursor: "pointer" }}
                    />
                  </Box>
                </Box>

                <Box sx={{ padding: "15px" }}>
                  <MDBox mb={2}>
                    <FormControl fullWidth>
                      <InputLabel>Select Device</InputLabel>
                      <Field
                        name="device"
                        as={Select}
                        fullWidth
                        label="Select Device"
                        style={{
                          height: "44px",
                          fontSize: "16px",
                          borderRadius: "5px",
                        }}
                        onChange={(e) => {
                          const selectedDeviceId = e.target.value;
                          setFieldValue("device", selectedDeviceId);
                          const device = data.find(
                            (d) => d.device_id === selectedDeviceId
                          );
                          setSelectedDevice(device);
                        }}
                      >
                        {sitesData?.map((x) => (
                          <MenuItem key={x.device_id} value={x.device_id}>
                            {x.device_name}
                          </MenuItem>
                        ))}
                      </Field>
                    </FormControl>
                  </MDBox>

                  {selectedDevice && (
                    <Box
                      sx={{
                        position: "relative",
                        width: "620px",
                        height: "auto",
                        display: "flex",
                        flexDirection: "column",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                        }}
                        className="alert-box"
                      >
                        <Alert
                          icon={<Info fontSize="inherit" />}
                          severity="warning"
                          sx={{
                            fontWeight: "500",
                            fontSize: "15px",
                            width: "100%",
                          }}
                        >
                          Draw a polygon on the given image and enter a custom
                          area name.
                        </Alert>
                      </Box>

                      <img
                        src={selectedDevice?.thumbnail || NoImage}
                        style={{
                          maxHeight: "300px",
                          objectFit: "contain",
                          marginTop: "25px",
                          marginBottom: "25px",
                          width: "100%",
                        }}
                        className="polygon-image"
                      />

                      <Box
                        sx={{
                          position: "absolute",
                          top: "76px",
                          left: "50%",
                          transform: "translateX(-50%)",
                          width: "534px",
                          height: "315px",
                          pointerEvents: "none",
                          zIndex: 3,
                        }}
                      >
                        <svg
                          width={480}
                          height={300}
                          style={{
                            width: "100%",
                            height: "100%",
                            position: "absolute",
                            top: 0,
                            left: 0,
                            pointerEvents: "none",
                          }}
                        >
                          {Object.entries(polygons).map(
                            ([className, points]) => {
                              if (!Array.isArray(points)) return null;

                              const scaledPoints = points.map((point) => ({
                                x: parseFloat(point.x) * 480,
                                y: parseFloat(point.y) * 300,
                              }));

                              const maxX = Math.max(
                                ...scaledPoints.map((pt) => pt.x)
                              );
                              const minY = Math.min(
                                ...scaledPoints.map((pt) => pt.y)
                              );

                              return (
                                <g
                                  key={className}
                                  style={{ pointerEvents: "none" }}
                                >
                                  <polygon
                                    points={scaledPoints
                                      .map((pt) => `${pt.x},${pt.y}`)
                                      .join(" ")}
                                    fill="rgba(28, 180, 214, 0.2)"
                                    stroke="#1cb4d6"
                                    strokeWidth="2"
                                    style={{
                                      cursor: "pointer",
                                      pointerEvents: "auto",
                                    }}
                                  />

                                  <foreignObject
                                    x={maxX - 12}
                                    y={minY - 12}
                                    width={24}
                                    height={24}
                                    style={{
                                      pointerEvents: "auto",
                                      cursor: "pointer",
                                      zIndex: 10,
                                    }}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDeletePolygon(
                                        className,
                                        setFieldValue
                                      );
                                    }}
                                  >
                                    <div
                                      style={{
                                        width: 24,
                                        height: 24,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        background: "white",
                                        borderRadius: "50%",
                                        border: "2px solid #1cb4d6",
                                        boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                                      }}
                                    >
                                      <CloseOutlined
                                        style={{
                                          color: "#1cb4d6",
                                          fontSize: 16,
                                        }}
                                      />
                                    </div>
                                  </foreignObject>

                                  <foreignObject
                                    x={scaledPoints[0].x}
                                    y={scaledPoints[0].y - 25}
                                    width={100}
                                    height={20}
                                    style={{ pointerEvents: "none" }}
                                  >
                                    <div
                                      style={{
                                        background: "#1cb4d6",
                                        color: "white",
                                        padding: "2px 6px",
                                        borderRadius: "4px",
                                        fontSize: "12px",
                                        fontWeight: "bold",
                                        maxWidth: "100px",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                      }}
                                    >
                                      {className}
                                    </div>
                                  </foreignObject>
                                </g>
                              );
                            }
                          )}
                        </svg>
                      </Box>

                      {showClassSelector && (
                        <Box
                          sx={{
                            position: "absolute",
                            top: "50%",
                            left: "50%",
                            transform: "translate(-50%, -50%)",
                            background: "white",
                            padding: 3,
                            borderRadius: 2,
                            boxShadow: 3,
                            zIndex: 10,
                            minWidth: 300,
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              mb: 2,
                            }}
                          >
                            <Typography variant="h6">
                              Enter Area Name
                            </Typography>

                            <CloseOutlined
                              sx={{ cursor: "pointer" }}
                              onClick={handleCancelAreaSelection}
                            />
                          </Box>

                          <TextField
                            fullWidth
                            variant="outlined"
                            placeholder="Enter custom area name"
                            value={areaName}
                            onChange={(e) => setAreaName(e.target.value)}
                            onKeyPress={(e) => {
                              if (e.key === "Enter") {
                                handleAreaNameSubmit(setFieldValue);
                              }
                            }}
                            autoFocus
                            sx={{
                              mb: 2,
                              "& .MuiOutlinedInput-root": {
                                height: "44px",
                                fontSize: "16px",
                                borderRadius: "5px",
                              },
                            }}
                          />

                          <Box display="flex" gap={1} justifyContent="flex-end">
                            <MDButton
                              size="small"
                              variant="outlined"
                              onClick={handleCancelAreaSelection}
                              sx={{
                                fontSize: "14px",
                                height: "35px",
                                width: "100px",
                                borderRadius: "5px",
                                fontSize: "14px",
                                fontWeight: "600",
                                textTransform: "capitalize",
                                backgroundColor: "transparent",
                                color: "gray",
                                borderColor: "gray",
                                "&:hover": {
                                  backgroundColor: "transparent",
                                  color: "white",
                                  borderColor: "gray",
                                },
                              }}
                            >
                              Cancel
                            </MDButton>
                            <MDButton
                              size="small"
                              color="info"
                              variant="gradient"
                              onClick={() =>
                                handleAreaNameSubmit(setFieldValue)
                              }
                              disabled={!areaName.trim()}
                              sx={{
                                fontSize: "14px",
                                height: "35px",
                                width: "100px",
                                borderRadius: "5px",
                                fontSize: "14px",
                                fontWeight: "500",
                                textTransform: "capitalize",
                                backgroundColor: "#1cb4d6",
                                "&:hover": {
                                  backgroundColor: "#1791b0",
                                },
                              }}
                            >
                              Add
                            </MDButton>
                          </Box>
                        </Box>
                      )}

                      <Box
                        sx={{
                          position: "absolute",
                          top: "76px",
                          left: "50%",
                          transform: "translateX(-50%)",
                          width: "534px",
                          height: "315px",
                          zIndex: 2,
                        }}
                        className="polygon-canvas"
                      >
                        <ReactPolygonDrawer
                          key={drawerKey}
                          width={480}
                          height={300}
                          background={selectedDevice?.url}
                          disableAutoClosing={false}
                          lineColor="#1cb4d6"
                          pointColor="white"
                          fillColor="rgba(28, 180, 214, 0.2)"
                          disablePointInteraction={false}
                          restrictToImage={true}
                          maxPoints={10}
                          minPoints={3}
                          onFinish={handlePolygonComplete}
                          className="polygon-drawer"
                        />
                      </Box>
                    </Box>
                  )}

                  <MDBox mb={2} mt={1}>
                    <FormControl fullWidth>
                      <InputLabel>Select Service</InputLabel>
                      <Field
                        name="provided_service_type"
                        as={Select}
                        fullWidth
                        label="Select Service"
                        style={{
                          height: "44px",
                          fontSize: "14px",
                          borderRadius: "5px",
                        }}
                        value={values.provided_service_type}
                        onChange={(e) => handleServiceChange(e, setFieldValue)}
                      >
                        <MenuItem disabled>Select Service</MenuItem>
                        {services?.map((service) => (
                          <MenuItem key={service?.id} value={service?.id}>
                            {service?.name}
                          </MenuItem>
                        ))}
                      </Field>
                    </FormControl>
                  </MDBox>

                  <MDBox>
                    {selectedService &&
                      services
                        .find(
                          (service) => service.id === parseInt(selectedService)
                        )
                        ?.sub_services?.map((subService) => (
                          <div key={subService[1]} className="mb-2">
                            <label style={{ fontSize: "14px" }}>
                              <input
                                type="checkbox"
                                disabled
                                value={subService[1]}
                                checked={values.selectedSubServices.includes(
                                  subService[1]
                                )}
                                onChange={() =>
                                  handleSubServiceChange(
                                    subService[1],
                                    setFieldValue,
                                    values
                                  )
                                }
                                style={{ marginRight: "8px" }}
                              />
                              {subService[0]}
                            </label>
                          </div>
                        ))}
                  </MDBox>

                  <MDBox mt={4} mb={1}>
                    <Button
                      type="submit"
                      variant="gradient"
                      color="info"
                      fullWidth
                      disabled={loading || !dirty}
                      sx={{
                        height: "38px",
                        width: "100%",
                        fontSize: "14px",
                        fontWeight: "500",
                        background: !dirty
                          ? "#e0e0e0"
                          : `${gradients.info.main}`,
                        color: white.main,
                        border: "none",
                        marginRight: "0",
                        marginBottom: "0",
                        "&:hover": {
                          background: `${gradients.info.state} !important`,
                          color: white.main,
                        },
                      }}
                    >
                      {loading ? (
                        <CircularProgress
                          size={25}
                          sx={{ color: "white !important" }}
                        />
                      ) : editScenarioData ? (
                        "UPDATE"
                      ) : isSubmitting ? (
                        "Adding..."
                      ) : (
                        "ADD"
                      )}
                    </Button>
                  </MDBox>
                </Box>
              </Form>
            )}
          </Formik>
        </Box>
      </Modal>
    </React.Fragment>
  );
};

export default AddScenarioModal;
